import { AxiosRequestConfig, CancelToken } from 'axios';
import { mapValues, merge } from 'lodash';
import { pipe } from 'lodash/fp';

import api from '@portal/common/api/index';
import request from '@portal/common/api/request';
import { DATA_QUERY_TYPE } from '@portal/common/models/data-query';
import {
    AiChatFeedbackResponse,
    AiChatManualFiltersResponse,
    AiChatMessageResponse,
    AiChatSummary,
    AnnouncementResource,
    ConditionDetailNotes,
    CountryLocationSdi,
    DataExplorerSelectionResource,
    DataFilters,
    DataGranularityResponse,
    DataProject,
    DataQueryParams,
    DataQueryType,
    DataRequest,
    DataResponse,
    DataType,
    Entity,
    MethodologyResource,
    OrganizationMember,
    OrganizationMemberAPICredentials,
    PaginatedRequest,
    PaginatedResponse,
    PostAiChatManualFiltersParams,
    RawRaceEntity,
    Resource,
    ResourceList,
    Round,
    SSOEndpointData,
    SaveAiChatDataCollectionChoiceParams,
    SendPdfReportParams,
    SidebarLinkResource,
    SubmitAiChatFeedbackParams,
    SubmitAiChatMessageParams,
    WelcomeMessageResource,
} from '@portal/common/types';
import { serializeDataTypes } from '@portal/common/utility/ai-chat-content';
import {
    deserializeResource,
    deserializeRichText,
    serializeResource,
} from '@portal/common/utility/api-helpers';

import config from './config';
import {
    OAuthAuthorizationRedirect,
    OAuthAuthorizationRequest,
    OAuthAuthorizedClient,
} from './models/oauth';

const UPLOAD_TIMEOUT = 5 * 60 * 1000;
const DOWNLOAD_TIMEOUT = 3600 * 1000;
const AI_CHAT_TIMEOUT = 5 * 60 * 1000;
const DATASET_QUERY_TIMEOUT = 5 * 60 * 1000;

const serializeFilters = (filters: DataFilters) =>
    mapValues(filters, (values) => {
        if (values && !Array.isArray(values)) {
            throw Error('filter values are not an array');
        }
        return values && values.join(',');
    });

const postDatasetQuery = (
    resourceId: number,
    dataType: DataType,
    queryType: DataQueryType,
    filters: DataFilters,
    params: DataQueryParams = {},
    requestConfig?: AxiosRequestConfig
): Promise<DataResponse> =>
    request.post(
        `/my-organization/data-collection-resources/${resourceId}/datasets/${dataType}/${queryType}`,
        {
            ...serializeFilters(filters),
            ...params,
        },
        { timeout: DATASET_QUERY_TIMEOUT, ...requestConfig }
    );

api.setApiUrl(config.apiUrl);

export default merge(api, {
    auth: {
        getEndpoint: (email?: string): Promise<SSOEndpointData> =>
            request.get('oauth2/authorization-endpoint', { email }),
    },

    session: {
        signIn: (email, password) =>
            request.post('organization-member-sessions', { email, password }),
        signInWithSSO: (code, state) =>
            request.post('oauth2/organization-member-sessions', { code, state }),
    },

    oauth: {
        // Resolves to null when the request expired or was already answered.
        getAuthorizationRequest: (requestId: string): Promise<OAuthAuthorizationRequest | null> =>
            request.get(`oauth/authorization-requests/${requestId}`),
        approveAuthorizationRequest: (requestId: string): Promise<OAuthAuthorizationRedirect> =>
            request.post(`oauth/authorization-requests/${requestId}/approve`),
        denyAuthorizationRequest: (requestId: string): Promise<OAuthAuthorizationRedirect> =>
            request.post(`oauth/authorization-requests/${requestId}/deny`),
        getAuthorizedClients: (): Promise<OAuthAuthorizedClient[]> =>
            request.get('my-organization-member-account/oauth-clients'),
        revokeAuthorizedClient: (clientId: string): Promise<boolean> =>
            request.delete(`my-organization-member-account/oauth-clients/${clientId}`),
    },

    account: {
        // Current org member of the current session user.
        getMyMemberAccount: (): Promise<OrganizationMember> =>
            request.get(`my-organization-member-account`),
        updateMyMemberAccount: (data: Partial<OrganizationMember>): Promise<OrganizationMember> =>
            request.put(`my-organization-member-account`, data),
        setPassword: (old_password, password) =>
            request.put(`my-organization-member-account`, { password, old_password }),
    },

    pdfReport: {
        send: ({ email, file, fileName }: SendPdfReportParams) => {
            const data = new FormData();
            data.append('file', file, fileName);
            data.append('email', email);
            return request.post(`my-organization-member-account/pdf-reports`, data, {
                timeout: UPLOAD_TIMEOUT,
            });
        },
    },

    organizationMember: {
        add: (data) => request.post(`organization-members`, data),
        resetPassword: (email) => request.post('organization-member-passwords/' + email),
    },

    organization: {
        updateOrganization: (data) => request.put(`my-organization`, data),
        getOrganization: () =>
            request
                .get(`my-organization`, {
                    fields: 'email_domains,self_sign_up_mode,self_sign_up_token,licensed_until,license_expiration_warning,pharma_products_search_is_enabled,ai_chat_is_enabled',
                })
                .then(pipe(deserializeRichText('license_expiration_warning'))),

        // Pre-auth lookup by self-sign-up token (no session yet → cannot use my-organization)
        getOrganizations: ({ search, page, per_page, self_sign_up_token }) =>
            request.get('organizations', {
                search,
                page,
                per_page,
                self_sign_up_token,
            }),

        addOrganizationMember: (data): Promise<OrganizationMember> =>
            request.post(`my-organization/members`, data),
        bulkCreateOrganizationMembers: (members) =>
            request.patch(
                `my-organization/members`,
                members.map((member) => ({
                    op: 'add',
                    path: '/',
                    value: member,
                })),
                { timeout: 5 * 60 * 1_000 }
            ),
        // Arbitrary member (manager editing another member) — keeps the member id.
        setOrganizationMember: (
            memberId: number,
            data: Partial<OrganizationMember>
        ): Promise<OrganizationMember> => request.put(`my-organization/members/${memberId}`, data),
        deleteOrganizationMember: (memberId: number): Promise<OrganizationMember> =>
            request.delete(`my-organization/members/${memberId}`),
        getOrganizationMembers: ({ search, page, per_page }): Promise<OrganizationMember[]> =>
            request.get(`my-organization/members`, {
                search,
                page,
                per_page,
                fields: 'first_name,last_name,last_access,avatar_url,email',
            }),

        getPharmaProductConditions: (params: {
            search: string;
            signal?: AbortSignal;
        }): Promise<DataFilters> =>
            request.get(
                `my-organization/pharma-product-conditions`,
                { search: params.search },
                { signal: params.signal }
            ),

        setOrganizationMemberRole: (memberId, organization_role) =>
            request.put(`my-organization/members/${memberId}`, {
                organization_role,
            }),

        getOrganizationMemberAPICredentials: ({
            ...params
        }): Promise<PaginatedResponse<OrganizationMemberAPICredentials>> =>
            request.get(`my-organization-member-account/api-credentials`, params),
        addOrganizationMemberAPICredentials: ({
            data,
        }): Promise<OrganizationMemberAPICredentials> =>
            request.post(`my-organization-member-account/api-credentials`, data),
        removeOrganizationMemberAPICredentials: ({
            apiCredentialsId,
        }): Promise<OrganizationMemberAPICredentials> =>
            request.delete(`my-organization-member-account/api-credentials/${apiCredentialsId}`),

        getOrganizationDataExplorerSelections: ({
            ...params
        }: PaginatedRequest<DataExplorerSelectionResource>): Promise<
            PaginatedResponse<DataExplorerSelectionResource>
        > =>
            request
                .get(`my-organization/data-explorer-selection-resources`, {
                    fields: 'category{description},created,sort_order',
                    ...params,
                })
                .then((res) => {
                    return {
                        ...res,
                        results: res.results.map(deserializeResource),
                    };
                }),
        createOrganizationDataExplorerSelections: ({
            item,
        }: {
            item: DataExplorerSelectionResource;
        }): Promise<DataExplorerSelectionResource> =>
            request
                .post(`my-organization/data-explorer-selection-resources`, serializeResource(item))
                .then((res) => deserializeResource(res)),
        updateOrganizationDataExplorerSelections: ({
            item,
        }: {
            item: DataExplorerSelectionResource;
        }): Promise<DataExplorerSelectionResource> =>
            request
                .put(
                    `my-organization/data-explorer-selection-resources/${item.id}`,
                    serializeResource(item)
                )
                .then((res) => deserializeResource(res)),

        getOrganizationDataCollectionResourceRefinedGranularity: ({
            resourceId,
            data_type,
            round_id,
            primary_entity_id = null,
        }): Promise<DataGranularityResponse> =>
            request.post(
                `my-organization/data-collection-resources/${resourceId}/datasets/${data_type}/granularity`,
                {
                    round_id,
                    primary_entity_id,
                }
            ),
        regenerateSelfSignUpUrl: () => request.post(`my-organization/self_sign_up_token`, {}),

        // best-practice
        getAnnouncements: (
            paginationParams: PaginatedRequest<AnnouncementResource>
        ): Promise<PaginatedResponse<AnnouncementResource>> =>
            request.get(`my-organization/announcements`, {
                ...paginationParams,
                fields: 'is_live',
            }),
        getSidebarLinks: (
            paginationParams: PaginatedRequest<SidebarLinkResource>
        ): Promise<PaginatedResponse<SidebarLinkResource>> =>
            request.get(`my-organization/sidebar-links`, {
                ...paginationParams,
                fields: '',
            }),
        getWelcomeMessage: (): Promise<WelcomeMessageResource> =>
            request
                .get(`my-organization/welcome-message`, {
                    fields: 'creator{first_name,last_name,email},created,updated',
                })
                .then(pipe(deserializeRichText('text'), deserializeRichText('title'))),

        getOrganizationResource: ({ hash }: { hash: string }) =>
            request.get(`/my-organization/resources?hash=${hash}`),
    },

    organizationGroupContent: {
        getLinksAndFiles: ({
            ...params
        }: PaginatedRequest<ResourceList>): Promise<PaginatedResponse<ResourceList>> =>
            request.get(`my-organization/group-content-resources`, {
                ...params,
                fields: 'category{description},created,updated,sort_order',
                type: 'link,file',
            }),

        getLinks: ({
            ...params
        }: PaginatedRequest<ResourceList>): Promise<PaginatedResponse<ResourceList>> =>
            request.get(`my-organization/group-content-link-resources`, {
                ...params,
                fields: 'category{description},created,updated,sort_order',
            }),

        getFiles: ({
            ...params
        }: PaginatedRequest<ResourceList>): Promise<PaginatedResponse<ResourceList>> =>
            request.get(`my-organization/group-content-file-resources`, {
                ...params,
                fields: 'category{description},created,updated,sort_order',
            }),

        getDataCollections: ({ ...params }: Partial<PaginatedRequest<ResourceList>> = {}): Promise<
            PaginatedResponse<ResourceList>
        > =>
            request.get(`my-organization/group-content-data-collection-resources`, {
                page: 1,
                per_page: 1000,
                ...params,
                fields: ['created', 'sort_order']
                    .concat(params.fields ? params.fields : [])
                    .join(','),
            }),
    },

    permalink: {
        getPermalinks: ({
            ...params
        }: PaginatedRequest<Resource>): Promise<PaginatedResponse<Resource>> =>
            request
                .get(`my-organization-member-account/permalinked-resources`, {
                    fields: 'category{description},created,sort_order',
                    ...params,
                })
                .then((res) => {
                    return {
                        ...res,
                        results: res.results.map(deserializeResource),
                    };
                }),

        createPermalink: ({
            name,
            dataExplorerSelection,
        }: {
            name: string;
            dataExplorerSelection: DataExplorerSelectionResource;
        }): Promise<Resource> =>
            request
                .post(
                    `my-organization-member-account/permalinked-data-explorer-selection-resources`,
                    {
                        ...serializeResource(dataExplorerSelection),
                        name,
                    }
                )
                .then((res) => deserializeResource(res)),

        removePermalink: ({ permalinkId }: { permalinkId: number }): Promise<Resource> =>
            request
                .delete(`my-organization-member-account/permalinked-resources/${permalinkId}`)
                .then((res) => deserializeResource(res)),
    },
    organizationMemberFavoriteResource: {
        getFavoriteResources: ({
            ...params
        }: PaginatedRequest<Resource>): Promise<PaginatedResponse<Resource>> =>
            request
                .get(`my-organization-member-account/favorite-resources`, {
                    fields: 'category{description},created,updated,sort_order',
                    ...params,
                })
                .then((res) => {
                    return {
                        ...res,
                        results: res.results.map(deserializeResource),
                    };
                }),
        addResourceToFavorites: ({ item }: { item: Resource }): Promise<Resource> =>
            request
                .post(
                    `my-organization-member-account/favorite-resources/${item.id}`,
                    serializeResource(item)
                )
                .then((res) => deserializeResource(res)),
        createDataExplorerSelectionResourceAndAddItToFavorites: ({
            item,
        }: {
            item: Resource;
        }): Promise<Resource> =>
            request
                .post(
                    `my-organization-member-account/favorite-data-explorer-selection-resources`,
                    serializeResource(item)
                )
                .then((res) => deserializeResource(res)),
        removeResourceFromFavorites: ({ item }: { item: Resource }): Promise<Resource> =>
            request
                .delete(`my-organization-member-account/favorite-resources/${item.id}`)
                .then((res) => deserializeResource(res)),
    },

    organizationResourceList: {
        createOrganizationResourceList: ({
            item,
        }: {
            item: Partial<ResourceList>;
        }): Promise<ResourceList> => request.post(`my-organization/resource-lists`, item),
        updateOrganizationResourceList: ({
            listId,
            item,
        }: {
            listId: number;
            item: Partial<ResourceList>;
        }): Promise<ResourceList> =>
            request.put(`my-organization/resource-lists/${listId}`, {
                fields: 'resource_ids,creator',
                ...item,
            }),
        deleteOrganizationResourceList: ({ listId }: { listId: number }): Promise<ResourceList> =>
            request.delete(`my-organization/resource-lists/${listId}`),
    },

    organizationResourceListResource: {
        getResourceListResources: ({
            listId,
            ...params
        }: PaginatedRequest<Resource> & {
            listId: number;
        }): Promise<PaginatedResponse<Resource>> =>
            request
                .get(`my-organization/resource-lists/${listId}/resources`, {
                    fields: 'category{description},created,updated,sort_order',
                    ...params,
                })
                .then((res) => {
                    return {
                        ...res,
                        results: res.results.map(deserializeResource),
                    };
                }),
        addResourceToOrganizationResourceList: ({
            listId,
            item,
        }: {
            listId: number;
            item: Resource;
        }): Promise<Resource> =>
            request
                .post(
                    `my-organization/resource-lists/${listId}/resources/${item.id}`,
                    serializeResource(item)
                )
                .then((res) => deserializeResource(res)),
        createDataExplorerSelectionResourceAndAddItToOrganizationResourceList: ({
            listId,
            item,
        }: {
            listId: number;
            item: Resource;
        }): Promise<Resource> =>
            request
                .post(
                    `my-organization/resource-lists/${listId}/data-explorer-selection-resources`,
                    serializeResource(item)
                )
                .then((res) => deserializeResource(res)),
        removeResourceFromOrganizationResourceList: ({
            listId,
            item,
        }: {
            listId: number;
            item: Resource;
        }): Promise<Resource> =>
            request
                .delete(`my-organization/resource-lists/${listId}/resources/${item.id}`)
                .then((res) => deserializeResource(res)),
    },

    organizationPublicResourceList: {
        getPublicResourceLists: ({
            ...params
        }: PaginatedRequest<ResourceList>): Promise<PaginatedResponse<ResourceList>> =>
            request.get(`my-organization/public-resource-lists`, params),

        makeResourceListPublic: (listId: number): Promise<ResourceList> =>
            request.post(`/my-organization/public-resource-lists/${listId}`, {
                fields: 'resource_ids',
            }),

        makeResourceListPrivate: (listId: number): Promise<ResourceList> =>
            request.delete(`/my-organization/public-resource-lists/${listId}`, {}),

        makeResourceListDefault: (listId: number): Promise<ResourceList> =>
            request.post(`/my-organization/public-resource-lists/${listId}/is_default`, {
                fields: 'resource_ids',
            }),

        makeResourceListNotDefault: (listId: number): Promise<ResourceList> =>
            request.delete(`/my-organization/public-resource-lists/${listId}/is_default`, {
                fields: 'resource_ids',
            }),
    },

    organizationMemberSubscribedResourceList: {
        subscribe: (listId: number): Promise<ResourceList> =>
            request.post(`/my-organization-member-account/subscribed-resource-lists/${listId}`, {}),

        unsubscribe: (listId: number): Promise<ResourceList> =>
            request.delete(
                `/my-organization-member-account/subscribed-resource-lists/${listId}`,
                {}
            ),

        getSubscribedResourceLists: ({
            ...params
        }: PaginatedRequest<ResourceList>): Promise<PaginatedResponse<ResourceList>> =>
            request
                .get(`my-organization-member-account/subscribed-resource-lists`, params)
                .then((res) => {
                    return {
                        ...res,
                        results: res.results.map(deserializeResource),
                    };
                }),
    },

    // Include subscribed lists + lists created by the organization member
    organizationMemberMyResourceList: {
        getMyResourceLists: ({
            ...params
        }: PaginatedRequest<ResourceList>): Promise<PaginatedResponse<ResourceList>> =>
            request.get(`my-organization-member-account/my-resource-lists`, params).then((res) => {
                return {
                    ...res,
                    results: res.results.map(deserializeResource),
                };
            }),
    },

    organizationMemberTickets: {
        createTicket: ({ ...params }): Promise<void> =>
            request.post(`my-organization-member-account/tickets`, params),
    },

    getPublicAdmins: ({ search, page, per_page }) =>
        request.get('public-admins', {
            search,
            page,
            per_page,
        }),

    data: {
        getCauseForecastFilters: () =>
            request.get('data/cause-forecast-filters', {}, { timeout: 100_000 }),
        getCauses: (): Promise<Entity[]> =>
            request
                .get('causes', {
                    page: 1,
                    per_page: 1000,
                })
                .then((res) => res.results),
        getCauseIcdCodes: (): Promise<Entity[]> => request.get('/cause-icd-codes'),
        getSequelas: (): Promise<Entity[]> =>
            request
                .get('sequelas', {
                    page: 1,
                    per_page: 3000,
                })
                .then((res) => res.results),
        getSdohs: (): Promise<Entity[]> =>
            request
                .get('sdohs', {
                    page: 1,
                    per_page: 3000,
                })
                .then((res) => res.results),
        getVehss: (): Promise<Entity[]> => request.get('vehss-conditions'),
        getCros: (): Promise<CroEntity[]> => request.get('cros'),
        getRei: (type): Promise<Entity[]> =>
            request
                .get('rei', {
                    type,
                    page: 1,
                    per_page: 1000,
                })
                .then((res) => res.results),
        queryDataCollectionResourceData: (
            resourceId: number,
            dataType: DataType,
            filters: DataFilters,
            requestConfig?: AxiosRequestConfig
        ): Promise<Record<string, DataResponse>> =>
            postDatasetQuery(
                resourceId,
                dataType,
                DATA_QUERY_TYPE.DATA,
                filters,
                {},
                requestConfig
            ).then((data) => ({ [dataType]: data })),
        queryDataCollectionByQueryType: (
            resourceId: number,
            dataType: DataType,
            queryType: DataQueryType,
            filters: DataFilters,
            params: DataQueryParams,
            requestConfig?: AxiosRequestConfig
        ): Promise<DataResponse> =>
            postDatasetQuery(resourceId, dataType, queryType, filters, params, requestConfig),
        getSingleYearAgeData: ({
            resourceId,
            dataType,
            filters,
            requestConfig,
        }): Promise<Record<string, DataResponse>> =>
            request.post(
                `/my-organization/data-collection-resources/${resourceId}/datasets/${dataType}/single-year-age/data`,
                serializeFilters(filters),
                { timeout: 300_000, ...requestConfig }
            ),
        getCustomAgeGroupData: ({
            resourceId,
            dataType,
            filters,
            requestConfig,
        }): Promise<Record<string, DataResponse>> =>
            request.post(
                `/my-organization/data-collection-resources/${resourceId}/datasets/${dataType}/custom-age-group/data`,
                serializeFilters(filters),
                { timeout: 300_000, ...requestConfig }
            ),
        getSingleYearAgeGranularity: ({
            resourceId,
            dataType,
            roundId,
            primaryEntityId,
        }): Promise<DataGranularityResponse> =>
            request.post(
                `/my-organization/data-collection-resources/${resourceId}/datasets/${dataType}/single-year-age/granularity`,
                {
                    round_id: roundId,
                    primary_entity_id: primaryEntityId,
                }
            ),
        // Methodology
        getMethodologies: (
            paginationParams: PaginatedRequest<MethodologyResource>
        ): Promise<PaginatedResponse<MethodologyResource>> =>
            request.get('methodologies', {
                ...paginationParams,
                fields: 'creator{first_name,last_name,email},created,updated,sort_order',
                order_by: 'sort_order',
            }),

        getForecastScenarios: () => request.get('data/forecast-scenarios'),
        // { location_id, age_group_id, gender_id, cause_id, measure_id, metric_id, forecast_scenario_id (optional), year (optional) }
        getCauseForecasts: (dataRequest: DataRequest): Promise<DataResponse> =>
            request.get(
                'data/cause-forecasts',
                {
                    location_id: config.globalMapCountryLocationsIds,
                    ...dataRequest,
                },
                { timeout: 30_000 }
            ),
        // /country-locations-sdi-quintiles
        getCountryLocationsSdi: (): Promise<CountryLocationSdi[]> =>
            request.get('country-locations-sdi-quintiles', {}, { timeout: 30_000 }),

        getDatasetGranularityKeyMetadata: (
            resourceId,
            dataType,
            granularityKey
        ): Promise<Record<string, DataResponse>> =>
            request.get(
                `/my-organization/data-collection-resources/${resourceId}/datasets/${dataType}/granularity/${granularityKey}/metadata`
            ),
        getRaces: (): Promise<RawRaceEntity[]> => request.get('races'),
    },

    dataProjects: {
        get: (): Promise<DataProject[]> => request.get('data-projects'),
    },

    rounds: {
        get: (): Promise<Round[]> => request.get('rounds'),
    },

    conditionDetailNotes: {
        get: ({ ...params }): Promise<ConditionDetailNotes[]> =>
            request.post('condition-detail-notes', params),
    },

    aiChat: {
        submitMessage: ({
            organizationId,
            chatId,
            prompt,
            dataCollectionId,
            signal,
        }: SubmitAiChatMessageParams): Promise<AiChatMessageResponse> => {
            const resource = chatId
                ? `/ai/organizations/${organizationId}/chats/${chatId}/messages`
                : `/ai/organizations/${organizationId}/chats`;
            return request.post(
                resource,
                { prompt, data_collection_id: dataCollectionId },
                { timeout: AI_CHAT_TIMEOUT, signal }
            );
        },
        saveDataCollectionChoice: ({
            organizationId,
            chatId,
            responseId,
            dataCollectionId,
        }: SaveAiChatDataCollectionChoiceParams): Promise<boolean> =>
            request.put(
                `/ai/organizations/${organizationId}/chats/${chatId}/responses/${responseId}/data-collection`,
                null,
                { params: { data_collection_id: dataCollectionId } }
            ),
        postManualFilters: ({
            organizationId,
            chatId,
            dataCollectionId,
            dataTypes,
            visualization,
            filters,
            signal,
        }: PostAiChatManualFiltersParams): Promise<AiChatManualFiltersResponse | null> =>
            request.post(
                `/ai/organizations/${organizationId}/chats/${chatId}/manual-filters`,
                {
                    data_collection_id: dataCollectionId,
                    data_type: serializeDataTypes(dataTypes),
                    visualization,
                    filters: serializeFilters(filters),
                },
                { signal }
            ),
        submitFeedback: ({
            organizationId,
            chatId,
            responseId,
            reaction,
            comment,
            categories,
        }: SubmitAiChatFeedbackParams): Promise<AiChatFeedbackResponse> =>
            request.put(
                `/ai/organizations/${organizationId}/chats/${chatId}/responses/${responseId}/feedbacks`,
                { reaction, comment, categories }
            ),
        getAll: (organizationId: number): Promise<AiChatSummary[]> =>
            request.get(`/ai/organizations/${organizationId}/chats`),
        delete: (organizationId: number, chatId: string): Promise<void> =>
            request.delete(`/ai/organizations/${organizationId}/chats/${chatId}`),
    },

    analytics: {
        visits: (params): Promise<void> => request.post('stats/visits', params),
        pageViews: (params): Promise<void> => request.post('stats/page-views', params),
        fileDownloads: (params): Promise<void> => request.post('stats/file-downloads', params),
        dataQueries: (params): Promise<void> => request.post('stats/data-queries', params),
        dataExport: (params): Promise<void> => request.post('stats/data-exports', params),
        pdfExport: (params): Promise<void> => request.post('stats/pdf-exports', params),
        pdfReport: (params): Promise<void> => request.post('stats/pdf-reports', params),
        ticketCreate: (params): Promise<void> => request.post('stats/tickets', params),
        conditionSearches: (params): Promise<void> =>
            request.post('stats/condition-searches', params),
    },

    // TODO: move to common
    downloadFile: ({ url, onProgress, cancelController }) =>
        request.get(url, null, {
            responseType: 'blob',
            onDownloadProgress: onProgress,
            timeout: DOWNLOAD_TIMEOUT,
            transformResponse: [
                (payload, headers) => {
                    if (headers['content-type'] === 'application/json') {
                        throw Error(
                            'Server download error. Please try again later or contact IHME support.'
                        );
                    }

                    if (headers['content-type'] === 'application/octet-stream') {
                        const filenameRegExp =
                            /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/;

                        const [_0, _1, quotedMatch, unquotedMatch] =
                            filenameRegExp.exec(headers['content-disposition']) || [];

                        if (!quotedMatch && !unquotedMatch) {
                            throw Error('Error parsing Content-Disposition header from the server');
                        }

                        const name = quotedMatch || unquotedMatch;
                        const data = new Blob([payload]);

                        return {
                            data: {
                                name,
                                data,
                            },
                        };
                    }

                    return payload;
                },
            ],
            cancelToken: new CancelToken((cancelCallback) => {
                if (cancelController) {
                    cancelController.cancel = cancelCallback;
                }
            }),
        }),

    // TODO: move to common
    uploadFile: ({ file, onProgress, cancelController }) => {
        const formData = new FormData();
        formData.append('file', file);

        return request.post('uploads', formData, {
            onUploadProgress: onProgress,
            transformRequest: [
                (payload, headers) => {
                    headers.Accept = 'text/plain';
                    return payload;
                },
            ],
            timeout: UPLOAD_TIMEOUT,
            responseType: 'text',
            transformResponse: [
                (payload) => {
                    const data = payload.trim().split('\n').map(JSON.parse);
                    return { data };
                },
            ],
            cancelToken: new CancelToken((cancelCallback) => {
                if (cancelController) {
                    cancelController.cancel = cancelCallback;
                }
            }),
        });
    },

    getUploadedFilePreviewUrl: (uploadedFileId: number): Promise<string> =>
        request.get(`/uploads/${uploadedFileId}/view`),
});
